import type { AgentChatThread } from '@/lib/api/threads'

/** Cursor-style sidebar date buckets. */
export type ChatDateRangeBucket = 'today' | 'yesterday' | 'last7' | 'last30' | 'older'

export const CHAT_DATE_RANGE_LABELS: Record<ChatDateRangeBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  older: 'Older',
}

export const CHAT_DATE_RANGE_ORDER: ChatDateRangeBucket[] = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'older',
]

/** Items shown per section before "... More". */
export const CHAT_SIDEBAR_SECTION_PREVIEW = 5

/** Sidebar section keys (date buckets + pinned). */
export type ChatSidebarSectionKey = ChatDateRangeBucket | 'pinned'

/** Older buckets start collapsed (Cursor-style). */
export const CHAT_SIDEBAR_DEFAULT_COLLAPSED: ChatSidebarSectionKey[] = [
  'last7',
  'last30',
  'older',
]

export function splitPinnedThreads(threads: AgentChatThread[]): {
  pinned: AgentChatThread[]
  unpinned: AgentChatThread[]
} {
  const pinned: AgentChatThread[] = []
  const unpinned: AgentChatThread[] = []
  for (const thread of threads) {
    if (thread.pinned) pinned.push(thread)
    else unpinned.push(thread)
  }
  return { pinned, unpinned }
}

/**
 * Cursor-style progressive density: subtitle on pinned + recent buckets.
 * Global dense mode forces single-line rows everywhere.
 */
export function shouldShowThreadSubtitle(
  section: ChatSidebarSectionKey,
  options?: { dense?: boolean },
): boolean {
  if (options?.dense) return false
  return section === 'pinned' || section === 'today' || section === 'yesterday'
}

export function threadActivityIso(thread: AgentChatThread): string {
  return thread.lastMessageAt ?? thread.updatedAt ?? thread.createdAt
}

/**
 * Bump one loaded thread's activity timestamp.
 * Returns the same array when the id is missing or the timestamp is unchanged,
 * so a send does not replace the sidebar list or its section counts.
 */
export function touchThreadActivity(
  threads: AgentChatThread[],
  threadId: string,
  activityIso: string,
): AgentChatThread[] {
  const idx = threads.findIndex((t) => t.id === threadId)
  if (idx < 0) return threads
  const current = threads[idx]
  if (current.lastMessageAt === activityIso && current.updatedAt === activityIso) {
    return threads
  }
  const next = threads.slice()
  next[idx] = { ...current, lastMessageAt: activityIso, updatedAt: activityIso }
  return next
}

export function chatDateRangeBucket(iso: string, now = new Date()): ChatDateRangeBucket {
  const d = new Date(iso)
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)
  const startOfLast7 = new Date(startOfToday)
  startOfLast7.setDate(startOfLast7.getDate() - 7)
  const startOfLast30 = new Date(startOfToday)
  startOfLast30.setDate(startOfLast30.getDate() - 30)

  if (d >= startOfToday) return 'today'
  if (d >= startOfYesterday) return 'yesterday'
  if (d >= startOfLast7) return 'last7'
  if (d >= startOfLast30) return 'last30'
  return 'older'
}

export type ChatThreadDateGroup = {
  bucket: ChatDateRangeBucket
  label: string
  items: AgentChatThread[]
}

/** Group threads into Cursor-style date range sections (newest first within each). */
export function groupThreadsByDateRange(
  threads: AgentChatThread[],
  now = new Date(),
): ChatThreadDateGroup[] {
  const buckets = new Map<ChatDateRangeBucket, AgentChatThread[]>()
  for (const bucket of CHAT_DATE_RANGE_ORDER) {
    buckets.set(bucket, [])
  }

  const sorted = [...threads].sort(
    (a, b) =>
      new Date(threadActivityIso(b)).getTime() - new Date(threadActivityIso(a)).getTime(),
  )

  for (const thread of sorted) {
    const bucket = chatDateRangeBucket(threadActivityIso(thread), now)
    buckets.get(bucket)!.push(thread)
  }

  return CHAT_DATE_RANGE_ORDER.filter((bucket) => (buckets.get(bucket)?.length ?? 0) > 0).map(
    (bucket) => ({
      bucket,
      label: CHAT_DATE_RANGE_LABELS[bucket],
      items: buckets.get(bucket)!,
    }),
  )
}
